import type { PageExperience } from '@/features/experiences/types';
import {
  toggleFilterId,
  useExperienceEventFilters,
} from '@/features/planner/hooks/useExperienceEventFilters';
import { Button, Flex } from '@/ui';
import { ViewKeyChip } from './ViewKeyChip';
import { VIEW_KEY_ROW_ID } from './viewKeyRowId';

type Props = {
  experience: PageExperience;
  // Resolved by useKeyCategories in the bar, which needs the same answer to decide
  // whether the Key is worth offering at all.
  categories: PageExperience['categories'];
  onClose: () => void;
};

// The Key's contents: what each category color means on the view below, and a
// one-click filter for each.
//
// Deliberately not the printable Legend. That one is sized in em against the
// sheet's font scale and styled through vibe('Legend'), and the bar is app chrome
// that must read the same whatever vibe the planner wears. They share the color
// lookup, which is the part that has to agree.
export const ViewKeyRow = ({ experience, categories, onClose }: Props) => {
  const { filterIds, hasAppliedFilters, setFilters, clearFilters } =
    useExperienceEventFilters(experience);

  const selectedIds = filterIds.categories;
  // An empty selection filters nothing, so every chip reads as showing rather
  // than as switched off — the same thing the events on the view are doing.
  const isSelected = (categoryId: string) =>
    selectedIds.length === 0 || selectedIds.includes(categoryId);

  // The same toggle the Filter popover's category rows use, on the same param, so
  // the two controls build one selection instead of competing.
  const handleToggle = (categoryId: string) =>
    setFilters({
      ...filterIds,
      categories: toggleFilterId(selectedIds, categoryId),
    });

  return (
    // Chips and actions are siblings in one wrapping row, so the actions only
    // take a line of their own once the chips fill it — nesting the chips, or
    // forcing a column on mobile, cost a second line even for two chips.
    // `ml="auto"` right-aligns them on whichever line they land on.
    <Flex
      align="center"
      flexWrap="wrap"
      gap={2}
      id={VIEW_KEY_ROW_ID}
      minW={0}
      py="2"
      w="100%"
    >
      {categories.map((category) => (
        <ViewKeyChip
          category={category}
          isSelected={isSelected(category.id)}
          key={category.id}
          onToggle={handleToggle}
        />
      ))}
      <Flex align="center" flexShrink={0} gap={2} ml="auto">
        {/* Clears every facet, not just categories — the same reset the bar's
            ActiveFiltersPill performs, offered where the user's attention is. */}
        {hasAppliedFilters && (
          <Button size="ah-sm" variant="ahGhost" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <Button size="ah-sm" variant="ahGhost" onClick={onClose}>
          Close
        </Button>
      </Flex>
    </Flex>
  );
};
